import { Sparkle } from "@/components/icons/customIcons.tsx";
import { cn } from "@/lib/utils.ts";
import { ArrowUpRight } from "lucide-react";
import type { ComponentType } from "react";

/** Inline "✨ Instant Book" marker for an autoSchedule kickoff. Naked icon +
 *  brand-tinted text, no pill — mirrors the thread status chips in
 *  `threads/shared.tsx`. `brand-700` (a couple shades darker than the box
 *  tint's `primary`) keeps the small label legible. */
const InstantBookBadge = () => (
  <span className="inline-flex items-center gap-1 text-xs font-medium text-brand-700 shrink-0">
    <Sparkle className="size-3" />
    Instant Book
  </span>
);

/**
 * Compact in-chat chip representation of an artifact: icon + title +
 * statusLabel, tap opens it. The thread never renders a full inline heavy
 * artifact; the expanded surface (canvas or lightbox) is the only place the full
 * form lives.
 *
 * Its own module rather than a `HeavyArtifactItem` local so a light artifact can
 * use it without pulling that file's render graph. An answered `userResponse`
 * form is the case in point: it is as settled as a sent draft, so it collapses to
 * the same row instead of leaving a spent form open in the thread.
 */
export const ArtifactChip = ({
  icon: Icon,
  title,
  statusLabel,
  isFinalStatus,
  isCancelled,
  isInstantBook,
  onOpen,
}: {
  icon: ComponentType<{ className?: string }>;
  title: string;
  statusLabel: string;
  /** Settled — dimmed, since there is nothing left to do with it. */
  isFinalStatus: boolean;
  isCancelled: boolean;
  isInstantBook?: boolean;
  onOpen: () => void;
}) => {
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`Open ${title}`}
      className={cn(
        "w-full flex flex-row gap-2 items-center rounded-xl border bg-popover text-popover-foreground px-3 py-3 text-left font-medium hover:bg-muted transition-colors",
        isFinalStatus && "opacity-60",
        isCancelled && "grayscale",
      )}
    >
      <Icon className="size-3.5 shrink-0" />
      <div className="flex flex-row items-center gap-2 min-w-0 flex-1">
        <span className="text-xs truncate">{title}</span>
        {isInstantBook && <InstantBookBadge />}
      </div>
      <div className="text-xs text-muted-foreground shrink-0">{statusLabel}</div>
      {/* The chip is one target, so this is an affordance rather than a second
          button: it says the row expands without nesting a control inside a
          control or shrinking a full-width hit area down to an icon.

          A bare arrow, not one leaving a box: the boxed version is how the web
          says "new tab", and this opens a panel a few hundred pixels to the
          right. Sized to the artifact icon opposite it rather than to
          `ArtifactBiscuit`'s expand button, which sits among larger chrome. */}
      <ArrowUpRight className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
    </button>
  );
};
